import React, { useCallback, useEffect, useRef } from 'react';
import { GripVertical, Trash2 } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface PresetTask {
  task_id: string;
  task_title: string;
}

interface TasksEditorProps {
  tasks: PresetTask[];
  onChange: (tasks: PresetTask[]) => void;
}

interface SortableTaskRowProps {
  task: PresetTask;
  index: number;
  onChange: (title: string) => void;
  onDelete: () => void;
}

const SortableTaskRow: React.FC<SortableTaskRowProps> = ({ task, index, onChange, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.task_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2"
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
      >
        <GripVertical className="w-5 h-5" />
      </div>

      {/* Task ID (read-only) */}
      <div className="text-sm font-medium text-gray-600 dark:text-gray-400 w-16">
        {task.task_id}
      </div>

      {/* Task Title Input */}
      <input
        type="text"
        value={task.task_title}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter task description..."
        data-task-id={task.task_id}
        className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />

      {/* Delete Button */}
      <button
        onClick={onDelete}
        className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors p-1"
        title="Delete task"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

export const TasksEditor: React.FC<TasksEditorProps> = ({ tasks, onChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Track which task should have focus after re-render
  const focusTaskIdRef = useRef<string | null>(null);

  // Auto-generate task ID based on current count
  const generateTaskId = useCallback(() => {
    const existingIds = tasks
      .filter(t => t && t.task_id) // Filter out undefined/null tasks
      .map(t => parseInt(t.task_id.replace('T', ''), 10))
      .filter(n => !isNaN(n));
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
    return `T${String(maxId + 1).padStart(3, '0')}`;
  }, [tasks]);

  // Update task title
  const handleTaskChange = useCallback((index: number, title: string, taskId?: string) => {
    const newTasks = [...tasks];
    
    // If typing in the empty row at the end, add it as a real task
    if (index >= tasks.length) {
      const newTaskId = generateTaskId();
      newTasks.push({
        task_id: newTaskId,
        task_title: title,
      });
      // Remember to focus this task after re-render
      focusTaskIdRef.current = newTaskId;
    } else {
      newTasks[index] = { ...newTasks[index], task_title: title };
      // Keep focus on this task
      if (taskId) {
        focusTaskIdRef.current = taskId;
      }
    }
    
    onChange(newTasks);
  }, [tasks, onChange, generateTaskId]);

  // Restore focus after render
  useEffect(() => {
    if (focusTaskIdRef.current) {
      const input = document.querySelector<HTMLInputElement>(
        `input[data-task-id="${focusTaskIdRef.current}"]`
      );
      if (input) {
        input.focus();
        // Move cursor to end
        const len = input.value.length;
        input.setSelectionRange(len, len);
      }
      focusTaskIdRef.current = null;
    }
  }, [tasks]);

  // Delete task
  const handleDeleteTask = useCallback((index: number) => {
    const newTasks = tasks.filter((_, i) => i !== index);
    onChange(newTasks);
  }, [tasks, onChange]);

  // Handle drag end
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = tasks.findIndex(t => t.task_id === active.id);
      const newIndex = tasks.findIndex(t => t.task_id === over.id);
      onChange(arrayMove(tasks, oldIndex, newIndex));
    }
  }, [tasks, onChange]);

  // Always show an empty row at the bottom for adding new tasks
  const displayTasks = [...tasks, { task_id: generateTaskId(), task_title: '' }];

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
        <p className="text-sm text-blue-900 dark:text-blue-200">
          Configure preset tasks that will be created automatically when this processor starts.
          These tasks help the LLM track progress and provide structure. Drag to reorder.
        </p>
      </div>

      {/* Task List */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tasks.map(t => t.task_id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {displayTasks.map((task, index) => {
              const isNewRow = index === tasks.length; // Last row is the empty one
              
              if (isNewRow) {
                // Empty row for adding new tasks
                return (
                  <div
                    key={`new-${task.task_id}`}
                    className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900 border border-dashed border-gray-300 dark:border-gray-600 rounded p-2"
                  >
                    <div className="text-gray-400 w-5 h-5" />
                    <div className="text-sm font-medium text-gray-400 w-16">
                      {task.task_id}
                    </div>
                    <input
                      type="text"
                      value=""
                      onChange={(e) => handleTaskChange(index, e.target.value)}
                      placeholder="Type to add a new task..."
                      data-task-id={`new-${task.task_id}`}
                      className="flex-1 px-3 py-1.5 text-sm border-0 rounded bg-transparent text-gray-500 dark:text-gray-400 italic focus:outline-none"
                    />
                    <div className="w-6" />
                  </div>
                );
              }

              return (
                <SortableTaskRow
                  key={task.task_id}
                  task={task}
                  index={index}
                  onChange={(title) => handleTaskChange(index, title, task.task_id)}
                  onDelete={() => handleDeleteTask(index)}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      {/* Task count */}
      {tasks.length > 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
          {tasks.length} task{tasks.length === 1 ? '' : 's'} configured
        </div>
      )}
    </div>
  );
};

